import { useMemo, useState } from 'react'
import { Alert, DatePicker, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { movementReason } from '@/features/inventory/movementReason'
import { queryKeys } from '@/shared/api/queryKeys'
import { optionalRangeToIso } from '@/shared/lib/dayRange'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { DataTable } from '@/shared/ui/table/DataTable'
import { PageHeader } from '@/shared/ui/PageHeader'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import {
  MOVEMENT_KINDS,
  fetchMovementPage,
  fetchStockItems,
  fetchWarehouses,
  isInbound,
  type MovementKind,
  type StockMovement,
} from '@/features/inventory/stockApi'

/**
 * Rang harakatning MA'NOSINI ko'rsatadi, turini emas:
 *   · kirim tomondagilar  — yashil
 *   · chiqim tomondagilar — ko'k (odatiy ish, xato emas)
 *   · yo'qotish           — qizil (ARALASHUV kerak)
 *   · inventarizatsiya    — sariq (kontragentsiz o'zgarish)
 */
const KIND_COLOR: Record<MovementKind, string> = {
  RECEIPT: 'green',
  OPENING: 'green',
  TRANSFER_IN: 'green',
  SALE_RETURN: 'green',
  ISSUE: 'blue',
  SALE: 'blue',
  TRANSFER_OUT: 'blue',
  INTERNAL_USE: 'purple',
  ADJUSTMENT: 'gold',
  DAMAGED: 'red',
  LOST: 'red',
}

/**
 * Harakatlar jurnali — O'ZGARMAS.
 *
 * <h2>⚠️ TAHRIRLASH VA O'CHIRISH TUGMASI YO'Q</h2>
 * Va u kelajakda ham qo'shilmaydi. Bazada `stock_movement_no_update`
 * triggeri `UPDATE` ni rad etadi (`ledger.entry` bilan bir xil qaror,
 * `docs/02-LEDGER.md` §2), API'da esa mos endpoint umuman yo'q. Xato
 * bo'lsa TESKARI harakat yoziladi — u ham jurnalda iz qoldiradi.
 *
 * Panelda tugma bo'lsa-yu, backend uni rad etsa, operator buni NUQSON
 * deb hisoblardi — holbuki bu qoidaning o'zi.
 *
 * <h2>⚠️ QIDIRUV MAYDONI YO'Q</h2>
 * `MovementQuery` matnli qidiruvni umuman olmaydi. Maydonni chizib, uni
 * jimgina e'tiborsiz qoldirish "qidirdim — topilmadi" degan YOLG'ON
 * javob bo'lardi. Filtrlar esa serverniki: ombor, mol, tur, sana.
 *
 * <h2>⚠️ MIQDOR ISHORALI</h2>
 * Musbat = kirim, manfiy = chiqim (`ledger.entry.amount` naqshi).
 * Panel ishorani QAYTA hisoblamaydi va tur bo'yicha xarita tuzmaydi:
 * u serverdan keladi va bazadagi `stock_movement_sign_matches_kind`
 * bilan kafolatlangan.
 *
 * <h2>⚠️ SANA — TENANT ZONASIDA</h2>
 * `occurredAt` va `createdAt` — `Instant`. Ular tenantning kun
 * zonasida ko'rsatiladi (`Asia/Kabul` UTC+4:30 — yarim soatlik
 * siljish, §19.1), UTC qiymati esa tooltipda qoladi.
 */
export function StockMovementsPage() {
  const { t } = useTranslation()

  // ⚠️ B4: `actor_id` da UUID turadi (gateway faqat shuni uzatadi).
  //    Nom identity ro'yxatidan keladi — bitta keshlangan so'rov.
  const actors = useActorDirectory()

  const [nodeId, setNodeId] = useState<string | null>(null)
  const [itemId, setItemId] = useState<string | null>(null)
  const [kind, setKind] = useState<MovementKind | null>(null)
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)

  const warehouses = useQuery({
    queryKey: queryKeys.stock.warehouses,
    queryFn: fetchWarehouses,
    staleTime: 5 * 60_000,
  })

  const items = useQuery({
    queryKey: queryKeys.stock.items(false),
    queryFn: () => fetchStockItems(false),
    staleTime: 5 * 60_000,
  })

  const warehouseName = useMemo(() => {
    const map = new Map<string, string>()
    for (const warehouse of warehouses.data ?? []) map.set(warehouse.id, warehouse.displayName)
    return map
  }, [warehouses.data])

  const itemLabel = useMemo(() => {
    const map = new Map<string, { name: string; sku: string; unit: string }>()
    for (const item of items.data ?? []) {
      map.set(item.id, { name: item.name, sku: item.sku, unit: item.unit })
    }
    return map
  }, [items.data])

  const filter = useMemo(
    () => ({
      nodeId,
      itemId,
      kind,
      // ⚠️ Kun chegarasi TENANT zonasida (`dayRange.ts`): ilgari
      // `startOf('day')` brauzernikini olardi va chegaradagi harakat
      // qo'shni kunga tushib qolardi.
      ...optionalRangeToIso(range),
    }),
    [nodeId, itemId, kind, range],
  )

  const fetchPage = useMemo(() => fetchMovementPage(filter), [filter])

  const columns = useMemo<ColumnsType<StockMovement>>(
    () => [
      {
        key: 'occurredAt',
        title: t('stock.field.occurredAt'),
        width: 180,
        render: (_, row) => (
          <Tooltip title={utcTooltip(row.occurredAt)}>
            <span>{formatDateTime(row.occurredAt)}</span>
          </Tooltip>
        ),
      },
      {
        key: 'kind',
        title: t('stock.field.kind'),
        width: 160,
        render: (_, row) => <Tag color={KIND_COLOR[row.kind]}>{t(`movementKind.${row.kind}`)}</Tag>,
      },
      {
        key: 'item',
        title: t('stock.field.item'),
        width: 200,
        render: (_, row) => {
          const label = itemLabel.get(row.itemId)
          // ⚠️ Xarita yuklanmagan yoki mol topilmagan bo'lsa — XOM `id`
          // emas, aniq belgi. Bo'sh katak "mol yo'q" degan noto'g'ri
          // xulosaga olib kelardi.
          if (!label) return <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
          return (
            <Space direction="vertical" size={0}>
              <Typography.Text>{label.name}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {label.sku}
              </Typography.Text>
            </Space>
          )
        },
      },
      {
        key: 'quantity',
        title: t('stock.field.quantity'),
        width: 140,
        render: (_, row) => (
          <Typography.Text type={isInbound(row) ? 'success' : undefined}>
            {`${row.quantity > 0 ? '+' : ''}${String(row.quantity)} ${itemLabel.get(row.itemId)?.unit ?? ''}`}
          </Typography.Text>
        ),
      },
      {
        key: 'node',
        title: t('stock.field.warehouse'),
        width: 220,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{warehouseName.get(row.nodeId) ?? '—'}</Typography.Text>
            {/* Ko'chirishning ikkinchi tarafi — usiz "ko'chirildi, qayerga?" */}
            {row.counterNodeId === null ? null : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('stock.field.counterNode', {
                  name: warehouseName.get(row.counterNodeId) ?? '—',
                })}
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        key: 'cost',
        title: t('stock.field.cost'),
        width: 150,
        render: (_, row) => formatMoneyValue(row.cost),
      },
      {
        key: 'docNo',
        title: t('stock.field.docNo'),
        width: 130,
        render: (_, row) => row.docNo ?? '—',
      },
      {
        key: 'reason',
        title: t('stock.field.reason'),
        render: (_, row) => {
          const reason = movementReason(row, t)
          const tag = row.assetTag === null ? null : t('stock.field.assetTagValue', { value: row.assetTag })
          return [reason, tag].filter(Boolean).join(' · ') || '—'
        },
      },
      {
        key: 'actorId',
        title: t('stock.field.actor'),
        width: 200,
        // ⚠️ B4: ilgari bu yerda `row.actorId ?? '—'` turardi, ya'ni
        //    ekranda XOM UUID. Nom `actorLabel` orqali keladi va u
        //    topilmasa qisqartirilgan bo'lakka tushadi — qiymat
        //    YO'QOLMAYDI (`actorName.ts` javadoc'i).
        render: (_, row) => actorLabel(row.actorId, actors, t('common.dash')),
      },
    ],
    [t, itemLabel, warehouseName, actors],
  )

  return (
    <>
      <PageHeader title={t('nav.stockMovements')} description={t('pageIntro.stockMovements')} />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('stock.movement.immutableNote')}
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select<string | null>
          allowClear
          showSearch
          optionFilterProp="label"
          value={nodeId}
          onChange={(value) => setNodeId(value ?? null)}
          placeholder={t('stock.balance.allWarehouses')}
          style={{ width: 250 }}
          loading={warehouses.isPending}
          options={(warehouses.data ?? []).map((warehouse) => ({
            value: warehouse.id,
            label: `${warehouse.displayName} (${warehouse.code})`,
          }))}
        />
        <Select<string | null>
          allowClear
          showSearch
          optionFilterProp="label"
          value={itemId}
          onChange={(value) => setItemId(value ?? null)}
          placeholder={t('stock.balance.allItems')}
          style={{ width: 250 }}
          loading={items.isPending}
          options={(items.data ?? []).map((item) => ({
            value: item.id,
            label: `${item.name} · ${item.sku}`,
          }))}
        />
        <Select<MovementKind | null>
          allowClear
          value={kind}
          onChange={(value) => setKind(value ?? null)}
          placeholder={t('stock.movement.allKinds')}
          style={{ width: 200 }}
          options={MOVEMENT_KINDS.map((item) => ({
            value: item,
            label: t(`movementKind.${item}`),
          }))}
        />
        <DatePicker.RangePicker
          value={range}
          onChange={(value) =>
            setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)
          }
        />
      </Space>

      <DataTable<StockMovement>
        queryKey={[...queryKeys.stock.movements, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="stock-movements"
        scrollX={1500}
        emptyTitle={t('stock.movement.empty')}
        emptyDescription={t('stock.movement.emptyHint')}
      />
    </>
  )
}
